// apps/web/src/features/crm/leads/activity/components/LeadNoteDrawer.tsx
//
// Capture drawer for a free-form note (#1809), persisted as a LeadComment
// via `POST /leads/:id/comments`. Mirrors `LeadTaskEditorDrawer`'s form
// pattern so the unsaved-changes guard is auto-wired: `useForm` lives in the
// SAME component that mounts `EditorDrawer` so the live dirty flag reaches
// the drawer's close guard.
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CreateLeadCommentRequestSchema, type CreateLeadCommentRequest } from '@magimp/shared';
import { useCreateLeadComment } from '@magimp/shared-hooks';
import {
  Button,
  EditorDrawer,
  FormRow,
  Textarea,
  useEditorGuard,
} from '../../../../../ui/index.js';
import { useToastStore } from '../../../../../stores/toast.js';
import { errorMessage } from '../../../../../api/errors.js';

export interface LeadNoteDrawerProps {
  leadId: string;
  onClose: () => void;
}

export function LeadNoteDrawer({ leadId, onClose }: LeadNoteDrawerProps) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const create = useCreateLeadComment(leadId);
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<CreateLeadCommentRequest>({
    resolver: zodResolver(CreateLeadCommentRequestSchema),
    defaultValues: { body: '' },
  });

  const onSubmit = (values: CreateLeadCommentRequest) => {
    create.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('leads:activity.note.toasts.created') });
        onClose();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorDrawer dirty={isDirty} busy={create.isPending} onConfirmClose={onClose} width={420}>
      <NoteFormBody
        isDirty={isDirty}
        busy={create.isPending}
        onSubmit={handleSubmit(onSubmit)}
        onClose={onClose}
      >
        <FormRow label={t('leads:activity.note.title')} error={errors.body?.message}>
          <Controller
            control={control}
            name="body"
            render={({ field }) => (
              <Textarea
                value={field.value}
                onChange={field.onChange}
                rows={5}
                placeholder={t('leads:activity.note.placeholder')}
                ariaLabel={t('leads:activity.note.title')}
              />
            )}
          />
        </FormRow>
      </NoteFormBody>
    </EditorDrawer>
  );
}

function NoteFormBody({
  isDirty,
  busy,
  onSubmit,
  onClose,
  children,
}: {
  isDirty: boolean;
  busy: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation(['leads', 'common']);
  // WHY: Cancel routes through confirmDestructive so a dirty form prompts before discarding.
  const { confirmDestructive } = useEditorGuard();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
      data-testid="lead-note-editor"
      data-dirty={isDirty ? 'true' : 'false'}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{t('leads:activity.note.title')}</h2>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button kind="ghost" onClick={() => confirmDestructive(onClose)} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" kind="primary" disabled={busy} data-testid="lead-note-save">
          {t('leads:activity.note.submit')}
        </Button>
      </div>
    </form>
  );
}
